// The fixed-width ruler (TO-400 BR-13): pure geometry over 1-based code-point
// columns. A boundary at column `c` starts a field at `c`; column 1 always
// starts the first field and `lineLength + 1` ends the last.

/** One fixed-width field cut from the ruler: 1-based start and width, in code points. */
export interface RulerSegment {
	start: number
	length: number
}

/** A boundary may sit on columns 2…lineLength (TO-400 BR-13). */
export const isBoundaryColumn = (column: number, lineLength: number): boolean =>
	Number.isInteger(column) && column >= 2 && column <= lineLength

/** Toggles the boundary at `column`; any other column leaves the boundaries unchanged. */
export const toggleBoundary = (boundaries: readonly number[], column: number, lineLength: number): number[] => {
	if (!isBoundaryColumn(column, lineLength)) return [...boundaries]
	const next = boundaries.includes(column) ? boundaries.filter((b) => b !== column) : [...boundaries, column]
	return next.sort((a, b) => a - b)
}

/** The segments between column 1, the boundaries and `lineLength + 1`. */
export const segmentsOf = (boundaries: readonly number[], lineLength: number): RulerSegment[] => {
	if (lineLength < 1) return []
	const starts = [1, ...boundaries.filter((b) => isBoundaryColumn(b, lineLength))]
	return starts.map((start, i) => ({ start, length: (starts[i + 1] ?? lineLength + 1) - start }))
}

/** The boundaries that reproduce contiguous fields (each start after the first). */
export const boundariesOf = (fields: readonly RulerSegment[]): number[] =>
	fields
		.map((f) => f.start)
		.filter((start) => start > 1)
		.sort((a, b) => a - b)
